import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';
import { WhatsAppCTA } from '../components/ui/whatsapp-cta';
import { renderTipTapToHTML } from '../utils/tiptapRenderer';
import { API_BASE } from '../config/api';

export const StaticPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${backendUrl}/api/public/pages/${slug}`);
      
      if (response.status === 404) {
        setError('Page not found');
        return;
      }
      
      if (!response.ok) throw new Error('Failed to fetch page');
      
      const data = await response.json();
      setPage(data.page);
      
      // Update document title
      document.title = data.page.seo_title || data.page.title || 'Page';
    } catch (err) {
      console.error('Error fetching page:', err);
      setError('Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  const handleConsultClick = () => {
    window.open('https://wa.me/60123456789?text=Hi, I would like to speak with an advisor', '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FFFDFB' }}>
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading page...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FFFDFB' }}>
        <Header />
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center">
            <svg className="w-16 h-16 mx-auto text-[#9CA3AF] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h1 className="text-2xl font-bold text-[#111827] mb-2" data-testid="error-title">{error}</h1>
            <p className="text-[#6B7280] mb-6">The page you're looking for doesn't exist or has been removed.</p>
            <Link 
              to="/" 
              className="px-6 py-3 bg-[#1E40AF] text-white font-semibold rounded-lg hover:bg-[#1e3a8a] transition-colors inline-block"
              data-testid="back-home-link"
            >
              Back to Home
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const renderContent = () => {
    if (!page.content) {
      return <p>No content available</p>;
    }

    // Handle structured content with sections
    if (page.content.sections) {
      return (
        <div className="space-y-8">
          {page.content.sections.map((section, index) => {
            switch (section.type) {
              case 'hero':
                return (
                  <div key={index} className="text-center py-8">
                    <h1 className="text-4xl md:text-5xl font-bold text-[#111827] mb-4">
                      {section.heading}
                    </h1>
                    {section.subheading && (
                      <p className="text-xl text-[#6B7280] mb-6">{section.subheading}</p>
                    )}
                    {section.cta && renderCTA(section.cta)}
                  </div>
                );
              
              case 'text':
                return (
                  <div 
                    key={index}
                    className="prose prose-lg max-w-none"
                    dangerouslySetInnerHTML={{ __html: section.content }}
                  />
                );
              
              case 'cta':
                return (
                  <div key={index} className="text-center py-8 bg-[#F9FAFB] rounded-xl p-8">
                    {section.heading && (
                      <h3 className="text-2xl font-bold text-[#111827] mb-6">{section.heading}</h3>
                    )}
                    {section.cta && renderCTA(section.cta)}
                  </div>
                );
              
              default:
                return null;
            }
          })}
        </div>
      );
    }

    // Fallback to TipTap rendering for legacy content
    if (typeof page.content === 'object') {
      return <div dangerouslySetInnerHTML={{ __html: renderTipTapToHTML(page.content) }} />;
    }

    return <p>No content available</p>;
  };

  const renderCTA = (cta) => {
    if (cta.type === 'popup' && cta.form_slug) {
      return (
        <button
          data-form-popup={cta.form_slug}
          className="px-8 py-4 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-semibold rounded-lg transition-colors text-lg"
          data-testid={`cta-popup-${cta.form_slug}`}
        >
          {cta.text || 'Click Here'}
        </button>
      );
    }

    if (cta.type === 'link' && cta.url) {
      return (
        <Link
          to={cta.url}
          className="inline-block px-8 py-4 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-semibold rounded-lg transition-colors text-lg"
          data-testid={`cta-link`}
        >
          {cta.text || 'Click Here'}
        </Link>
      );
    }

    return null;
  };

  const hasAdBanners = page.ad_banners && page.ad_banners.length > 0 &&
    page.ad_banners.some(banner => banner.image && banner.image.trim() !== '');

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FFFDFB' }}>
      <Header />

      {/* Featured Image - Full Width */}
      {page.featured_image && (
        <div className="w-full h-64 md:h-80 lg:h-96 overflow-hidden">
          <img
            src={page.featured_image}
            alt={page.title}
            className="w-full h-full object-cover"
            data-testid="featured-image"
          />
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 w-full max-w-full px-6 md:px-8 lg:px-12 py-8 md:py-12">
        <div className="w-full max-w-[1280px] mx-auto">
          
          {/* Breadcrumb */}
          <nav className="mb-6" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm">
              <li>
                <Link to="/" className="text-[#6B7280] hover:text-[#F97316] transition-colors">Home</Link>
              </li>
              <li className="text-[#9CA3AF]">/</li>
              <li className="text-[#111827] font-semibold truncate max-w-[200px] md:max-w-none">{page.title}</li>
            </ol>
          </nav>

          {/* 2-Column Layout (Desktop) / Single Column (Mobile) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            
            {/* LEFT COLUMN - CONTENT */}
            <article className={hasAdBanners ? 'lg:col-span-8' : 'lg:col-span-12'} data-testid="page-content-column">
              {/* Page Title */}
              <h1 
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#111827] mb-8 leading-tight"
                data-testid="page-title"
              >
                {page.title}
              </h1>
              
              {/* Content */}
              <div className="text-[#374151]" data-testid="page-content">
                {renderContent()}
              </div>
            </article>

            {/* RIGHT COLUMN - AD BANNERS (Desktop only when banners exist) */}
            {hasAdBanners && (
              <aside className="hidden lg:block lg:col-span-4" data-testid="ad-banners-sidebar">
                <div className="lg:sticky lg:top-24 space-y-4">
                  {/* Ad Banners */}
                  <div className="space-y-3">
                    {page.ad_banners
                      .filter(banner => banner.image && banner.image.trim() !== '')
                      .map((banner, index) => (
                        <a
                          key={index}
                          href={banner.target_url || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block rounded-lg overflow-hidden border border-[#E5E7EB] hover:shadow-lg transition-shadow"
                        >
                          <img
                            src={banner.image}
                            alt={`Advertisement ${index + 1}`}
                            className="w-full h-auto object-cover"
                          />
                        </a>
                      ))
                    }
                  </div>

                  {/* Quick Consultation Card */}
                  <div className="bg-gradient-to-br from-[#EFF6FF] to-[#DBEAFE] border-2 border-[#93C5FD] rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-10 h-10 bg-[#1E40AF] rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-[#111827] text-base">Need Guidance?</h3>
                        <p className="text-xs text-[#1E40AF]">Talk to our advisors</p>
                      </div>
                    </div>
                    <p className="text-[#4B5563] text-xs mb-4 leading-relaxed">
                      Get personalized advice on your study abroad journey.
                    </p>
                    <button
                      onClick={handleConsultClick}
                      data-testid="sidebar-consult-button"
                      className="w-full px-3 py-2 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white text-sm font-semibold rounded-lg transition-all"
                    >
                      Consult Now
                    </button>
                  </div>
                </div>
              </aside>
            )}
          </div>

          {/* Mobile Ad Banners - Below Content */}
          {hasAdBanners && (
            <div className="lg:hidden mt-12 space-y-4" data-testid="mobile-ad-banners">
              <h3 className="text-lg font-bold text-[#111827] mb-4">Sponsored</h3>
              <div className="grid grid-cols-2 gap-4">
                {page.ad_banners
                  .filter(banner => banner.image && banner.image.trim() !== '')
                  .slice(0, 3)
                  .map((banner, index) => (
                    <a
                      key={index}
                      href={banner.target_url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full rounded-xl overflow-hidden hover:opacity-90 transition-opacity"
                      style={{ aspectRatio: '1/1' }}
                    >
                      <img 
                        src={banner.image} 
                        alt={`Advertisement ${index + 1}`}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </a>
                  ))
                }
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
      <WhatsAppCTA phone="60123456789" message={`Hi, I have a question about: ${page.title}`} />
    </div>
  );
};

export default StaticPage;
